import React from "react";

import {
  Box,
  TimelineConnector,
  TimelineContent,
  TimelineItem,
} from "@chakra-ui/react";
import { IUserDetails } from "api/individual/types";
import { ReactComponent as BulletGreyIcon } from "assets/icons/bullet-grey.svg";
import { ReactComponent as GreenCheckIcon } from "assets/icons/green-check-circle.svg";
import { ReactComponent as OrangeTimerIcon } from "assets/icons/orange-timer-circle.svg";
import { ReactComponent as RedCloseIcon } from "assets/icons/red-close-cricle.svg";
import { ReactComponent as RedInfoOutlineIcon } from "assets/icons/red-info-outline.svg";
import { useIndividualCompliance } from "contexts/individual-compliance-context";
import { individualSectionTypeToLabelMap2 } from "hooks/useApproveOrRejectIndividualSection";
import { capitalizeName, formatDateToDDMMHHMM } from "utils";

import { Paragraph3, Paragraph4 } from "ui/typography";

import { IIndividualComplianceTrailTimelineItemProps } from "./types";

const START_INDEX = 0;

export const ComplianceTrailTimelineItem = (
  props: IIndividualComplianceTrailTimelineItemProps
) => {
  const {
    complianceTrail,
    isFirstComplianceTrailItem,
    isLastComplianceTrailItem,
  } = props;
  const { selectedIndividualDetails } = useIndividualCompliance();

  const { firstName, lastName } =
    (selectedIndividualDetails as IUserDetails) || {};

  const getIconWrapperClassName = (): string => {
    if (complianceTrail) {
      return "top-[10px]";
    } else if (isFirstComplianceTrailItem) {
      return "pt-[9px] top-3 bg-white";
    }
    return "top-[17px] bg-white";
  };

  const getStatusIcon = (status: string | undefined) => {
    switch (status) {
      case "approved":
        return <GreenCheckIcon />;
      case "pending":
        return <OrangeTimerIcon />;
      default:
        return <RedCloseIcon />;
    }
  };

  return (
    <TimelineItem css={{ display: "flex", gap: "7px" }}>
      <TimelineConnector>
        <Box
          position="relative"
          h="100%"
          className="w-6"
        >
          {!isLastComplianceTrailItem && (
            <Box
              w="1.5px"
              h="100%"
              position="absolute"
              top="0"
              left="calc(50% - 1px)"
              zIndex={0}
              className="bg-[#D1D5DB]"
            />
          )}
          <Box
            position="absolute"
            className={getIconWrapperClassName()}
            left="50%"
            transform="translate(-50%, -50%)"
            zIndex={1}
          >
            {getStatusIcon(complianceTrail.status)}
          </Box>
        </Box>
      </TimelineConnector>
      <TimelineContent css={{ paddingBottom: "34px" }}>
        <Box className="h-[95px] flex flex-col gap-4">
          <Box className="h-[39px] flex flex-col gap-[2px]">
            <Box className="flex gap-1 items-center">
              <Paragraph3
                textColor="#101828"
                fontWeight="500"
              >
                {individualSectionTypeToLabelMap2[
                  complianceTrail.documentName
                ] || "Not Applicable"}
              </Paragraph3>
              <BulletGreyIcon />
              <Paragraph3
                textColor="#101828"
                fontWeight="500"
                className="capitalize"
              >
                {complianceTrail?.status === "pending"
                  ? `User uploaded address document`
                  : complianceTrail?.status}
              </Paragraph3>
            </Box>
            <Paragraph4
              textColor="#667085"
              lineHeight="21px"
            >
              {complianceTrail?.timestamp &&
                formatDateToDDMMHHMM(complianceTrail?.timestamp)}
            </Paragraph4>
          </Box>
          <Box className="h-10 flex gap-[6px]">
            <Box
              className="w-8 h-8 rounded-[50%] text-[12.8px] leading-[19.2px] text-center text-white flex justify-center items-center"
              css={{
                backgroundColor: complianceTrail.bgColor,
              }}
            >
              {complianceTrail.adminName?.charAt(START_INDEX)?.toUpperCase() ||
                complianceTrail.commentBy?.charAt(START_INDEX)?.toUpperCase() ||
                "N"}
            </Box>
            <Box>
              <Paragraph3
                textColor="#101828"
                fontWeight="500"
                lineHeight="15.96px"
              >
                {complianceTrail.adminName ||
                  capitalizeName(firstName, lastName) ||
                  "N/A"}
              </Paragraph3>
              <Paragraph4
                textColor="#667085"
                lineHeight="21px"
              >
                {complianceTrail.commentBy || "N/A"}
              </Paragraph4>
            </Box>
          </Box>
        </Box>
        {complianceTrail.status === "rejected" && (
          <Box className="bg-[#FEF2F2] border-[#FFC5C5] border-[0.5px] py-3 px-[14px] rounded-xl flex items-center gap-[9px]">
            <RedInfoOutlineIcon className="shrink-0" />
            <Box className="flex flex-col gap-1">
              <Paragraph3
                textColor="#1D1D1D"
                fontWeight="500"
                lineHeight="15.96px"
              >
                Reason for Rejection
              </Paragraph3>
              <Paragraph3
                textColor="#1D1D1D"
                fontSize="13px"
                lineHeight="20px"
              >
                {complianceTrail.comment}
              </Paragraph3>
            </Box>
          </Box>
        )}
      </TimelineContent>
    </TimelineItem>
  );
};
